/** The app shell: the teal rail, the white top bar, and the grid the screens render into.
 *
 * `docs/design/30-design-principles.md`: a full-height shell with panels that scroll inside
 * themselves, and no page-level scroll. **Rebuilt for ADR-0212** (2026-11-01):
 *
 * - **The rail** is solid theme teal with white text, eighteen places in seven groups with six
 *   printed headings (`routeTable.ts` is the one list). It is icons (60px) under 1200px until the
 *   person presses the toggle, and then it is their choice, remembered; pointing at a collapsed rail
 *   opens it over the page without moving the page. No route changes its width.
 * - **The top bar** is white and holds, left to right: the rail's toggle, the one search (Cmd+K or
 *   Ctrl+K opens the palette), then - after the free space - the reporting context (the entity and
 *   the period), the help icon, the bell (Action items, with a count) and the person's menu. The
 *   screen's name is the page header's, drawn by `PageTitle`; the bar prints the `<h1>` itself only
 *   for a screen that has not moved to a page header yet.
 * - **The Scope 2 method toggle left the bar** (decision 5): one number everywhere by default. The
 *   `method` search parameter is still the carrier, so every link keeps the basis it was opened with;
 *   what sets it is My settings' Advanced switch.
 */

import {
  Buildings,
  CalendarBlank,
  CaretDown,
  Eye,
  IconContext,
  List,
  MagnifyingGlass,
  Question,
  Warning,
  X,
} from "@phosphor-icons/react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Link, useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import type { Entity, Membership, Organization, Scope2Method } from "../api/queries";
import { useEntities } from "../api/queries";
import { ReadFailed } from "../components/Loaded";
import { PageTitleContext } from "../components/ui/PageTitle";
import { StatusChip } from "../components/ui/StatusChip";
import type { PageTitleSlot } from "../components/ui/PageTitle";
import { shortDate } from "../lib/format";
import type { Window } from "../screens/useScreen";
import { customWindow } from "../screens/useScreen";
import { useDocumentTitle } from "./documentTitle";
import { ActionBell } from "./ActionBell";
import { Breadcrumbs } from "./Breadcrumbs";
import { CommandPalette } from "./CommandPalette";
import { LookupsFailed } from "./LookupsFailed";
import { useMe } from "../api/auth";
import { paletteScreens } from "./navItems";
import { fiscalWord } from "./fiscalWord";
import { shortcutLabel } from "./paletteModel";
import type { NavSection, RailSection, RecordPlace } from "./routeTable";
import { entryFor, labelOfPath, locate, navFor } from "./routeTable";
import { SectionTabs } from "./SectionTabs";
import { RAIL_ICONS_BELOW, readRailChoice, writeRailCollapsed } from "./railPrefs";
import { BADGES, railTabFor, useMissingSources } from "./RailBadges";
import { SessionBadge } from "./SessionBadge";
import styles from "./AppShell.module.css";

/** The rail's entries, chunked by section. The places, their order, their icons and the roles each
 * is offered to are all in `routeTable.ts` - this file draws them and decides nothing about them.
 * The palette is handed the screens of the *same* role filter (`paletteScreens`), so it can never
 * name a place the rail does not offer. */
function sectionsFor(role: string | undefined): ReadonlyArray<{
  section: RailSection;
  items: readonly NavSection[];
}> {
  const out: Array<{ section: RailSection; items: NavSection[] }> = [];
  for (const item of navFor(role)) {
    const last = out[out.length - 1];
    if (last && last.section === item.section) last.items.push(item);
    else out.push({ section: item.section, items: [item] });
  }
  return out;
}

// The workspace card is hidden when the rail becomes a horizontal strip. Move its one marker
// to a separate status row at that same breakpoint, outside both horizontal scroll containers.
const NARROW_RAIL = "(max-width: 900px)";
function subscribeNarrowRail(changed: () => void): () => void {
  const media = window.matchMedia(NARROW_RAIL);
  media.addEventListener("change", changed);
  return () => media.removeEventListener("change", changed);
}
const isNarrowRail = () => window.matchMedia(NARROW_RAIL).matches;
const serverNarrowRail = () => false;

// Under 1200px an unchosen rail starts as icons (60px). Asked as `max-width`, so a browser that cannot
// answer (and the test DOM) reads as wide and the rail starts open.
const SMALL_WINDOW = `(max-width: ${String(RAIL_ICONS_BELOW - 1)}px)`;
function subscribeSmallWindow(changed: () => void): () => void {
  const media = window.matchMedia(SMALL_WINDOW);
  media.addEventListener("change", changed);
  return () => media.removeEventListener("change", changed);
}
const isSmallWindow = () => window.matchMedia(SMALL_WINDOW).matches;

interface Props {
  title: string;
  crumb?: string;
  memberships: readonly Membership[];
  /** Set when the list of workspaces could not be read, **with the way to ask again** — one prop
   * and not two, so there is no failure without a retry. Without it an empty `memberships` is
   * indistinguishable from a person who works in one workspace, and the switcher just vanished. */
  membershipsFailed?: { error: unknown; onRetry: () => void } | null;
  organization: Organization | undefined;
  onOrganizationChange: (id: string) => void;
  method: Scope2Method;
  /** Kept in the contract and not drawn: the toggle left the bar (ADR-0212 decision 5), and the
   * Advanced switch in My settings is what changes the basis now. */
  onMethodChange?: (method: Scope2Method) => void;
  /** The reporting window every figure below is for, and every window this workspace has.
   *
   * In the top bar because it changes every figure on every screen. It replaced a pill reading "FY2025 · collecting", which was a hard-coded year beside
   * numbers that came from a hard-coded year — so the two agreed, and neither was true of a
   * Californian subsidiary with a March year end (finding F11).
   *
   * **`undefined` when there is none**, which is a workspace that has not declared a reporting
   * period. The selector then says so rather than offering a 1970 date to choose from. */
  period: Window | undefined;
  periods: readonly Window[];
  onPeriodChange: (period: Window) => void;
  /** Let the body scroll rather than fit the viewport (CLS-01).
   *
   * For a screen with more panels than the two-row grid holds. Opt-in, because making every
   * screen scroll is a visible change to all of them — and it is the **body** that scrolls, never
   * the page, so the shell's rule holds.
   */
  bodyScrolls?: boolean;
  /** For a screen that serves many places from one path (a record): the place it belongs to, which
   * the page knows and the address does not. See `recordPlace`. */
  lights?: RecordPlace;
  children: ReactNode;
}

/** The entity the figures are about (ADR-0035). **Plain text and not a pill**: it cannot be changed,
 * and a bordered chip with an icon beside the period selector is a control that does nothing
 * (review finding F21). Everything that *is* a control in the bar has a border, a height of 30px and
 * a hover; this has none of them, on purpose.
 *
 * **An authorization surface, not a filter.** The list comes from the server under RLS; the shell
 * renders what it is given and never asks for an entity by id, because "which entity may I see" is
 * a membership question and a URL is not allowed to answer it.
 *
 * It is read-only today. Choosing an entity narrows the inventory, and that narrowing belongs with
 * the register — the step after this one — so wiring a control that changes nothing would be worse
 * than showing the context it already has.
 */
function EntityContext({
  entities,
  failed,
}: {
  entities: readonly Entity[];
  /** Whether the read failed. **Not the same as a workspace with no companies** — this chip
   * disappeared for both, so a dropped request quietly removed the top bar's statement of what
   * every figure on every screen is about (EXP-29). It is said rather than left blank (design
   * rule 5), and it is said quietly, because it is the shell and it would otherwise shout on
   * every screen at once. */
  failed: boolean;
}) {
  if (failed) {
    return (
      <span className={styles.entity} data-testid="entity-context-failed">
        <Buildings className={styles.chipIcon} size={14} aria-hidden="true" />
        <span className={styles.entityText}>
          Companies unavailable
          <span className={styles.entityNote}>the entity context could not be read</span>
        </span>
      </span>
    );
  }
  if (entities.length === 0) return null;
  const withSites = entities.filter((entity) => entity.facility_count > 0);
  const only = entities.length === 1 ? entities[0] : undefined;
  const label = only ? only.legal_name : `All entities · ${entities.length}`;
  return (
    <span className={styles.entity} data-testid="entity-context">
      <Buildings className={styles.chipIcon} size={14} aria-hidden="true" />
      <span className={styles.entityText}>
        {label}
        {/* An entity with no sites contributes no figures. Saying so here stops somebody picking
            one later and concluding the inventory is empty. */}
        {withSites.length < entities.length ? (
          <span className={styles.entityNote}>
            {entities.length - withSites.length} with no sites
          </span>
        ) : null}
      </span>
    </span>
  );
}

/** The selector's one value that is not a window. A sentinel rather than `""`, because `""` is
 * what a `<select>` reports for *no option matched* and the two must not be the same thing. */
const CUSTOM = "custom-range";

/** The selector's value when no window is in force. Its own option, for `CUSTOM`'s reason: a
 * `<select>` whose value matches nothing renders with nothing chosen, and the one thing this
 * control must never do is say nothing about which period the figures are for. It is not a window
 * and `onChange` never treats it as one. */
const NO_PERIOD = "no-period";

/** A reporting window somebody types, for the periods the workspace has not declared — **EXP-21**.
 *
 * The acceptance clause asks to *"filter periods"*, and the selector offered exactly the windows
 * `organization.periods` holds: a filing year per (regime, legal entity) pair. That is the right
 * default and it is not the whole question. A controller comparing a quarter against the one before
 * it, or looking at the stub period between a year end and an acquisition, had **no control at
 * all** — only a URL they would have to know how to write, since `resolveWindow` has honoured
 * arbitrary dates since F11. The capability was reachable by hand and by nothing on screen, which
 * is the same shape as a guard nothing calls.
 *
 * Four things it has to do, and each of them is an EXP-29 dimension rather than decoration:
 *
 * - **Refuse rather than apply nonsense.** An end before a start selects no figures at all, and an
 *   empty register is the one state in which a reader concludes their data is missing. So it says
 *   what is wrong and navigates nowhere — the figures on screen are still the figures the top bar
 *   is describing.
 * - **Cancel restores**, rather than clearing the boxes. Opening the form, typing two dates and
 *   thinking better of it must leave the window exactly as it was.
 * - **Clearing it is choosing a declared period**, which the selector beside it does. A separate
 *   "clear" would be a second way to do one thing, and the two would disagree about what "no
 *   custom range" means.
 * - **Focus comes back to the control that opened it** (design rule 10). The form replaces the
 *   period selector, so applying or cancelling unmounts the thing the reader's focus is inside.
 */
function CustomRange({
  period,
  open,
  close,
  onPeriodChange,
}: {
  period: Window | undefined;
  /** Lifted, because the form **replaces** the period selector rather than sitting beside it: the
   * selector is where it is opened from, and two period controls side by side would be two answers
   * to one question. */
  open: boolean;
  /** Close, and put focus back on the selector (design rule 10 — a control that unmounts itself
   * has to give focus somewhere, and the somewhere is what opened it). Owned by the shell because
   * the element to return to is the one this form replaced. */
  close: () => void;
  onPeriodChange: (period: Window) => void;
}) {
  // **Seeded from the window in force, every time it opens** — see the `key` on the call site. The
  // commonest custom range is a nudge to the window a reader is already looking at, and an empty
  // pair of boxes asks them to retype both.
  const [from, setFrom] = useState(period?.period_start ?? "");
  const [to, setTo] = useState(period?.period_end ?? "");
  const [refused, setRefused] = useState<string | null>(null);
  // **Focus moves into the form when it opens** (F17). The period selector is unmounted while the
  // form is open, so the element the reader was on is destroyed; without this focus fell to the
  // first control on the page. `closeRange` pays the return trip.
  const firstDate = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) firstDate.current?.focus();
  }, [open]);

  const apply = () => {
    if (!from || !to) {
      setRefused("Both dates are needed. A window with one end selects nothing.");
      return;
    }
    if (to < from) {
      setRefused(
        `The end of the window is before its start: ${shortDate(to)} comes before ${shortDate(from)}.`,
      );
      return;
    }
    close();
    onPeriodChange(customWindow(from, to));
  };

  if (!open) return null;

  return (
    <span className={styles.range} data-testid="custom-range">
      {/* Visible words beside each date, because the browser's own field shows only a locale
          format (`01/01/2025`) and a pair of dates with nothing naming them is a guess. The
          `aria-label` stays: it is the control's accessible name and what the tests find it by. */}
      <label className={styles.rangeField}>
        <span aria-hidden="true">From</span>
        <input
          ref={firstDate}
          type="date"
          className={styles.rangeDate}
          aria-label="Window start"
          data-testid="custom-range-from"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
        />
      </label>
      <label className={styles.rangeField}>
        <span aria-hidden="true">To</span>
        <input
          type="date"
          className={styles.rangeDate}
          aria-label="Window end"
          data-testid="custom-range-to"
          value={to}
          onChange={(event) => setTo(event.target.value)}
        />
      </label>
      <button
        type="button"
        className={styles.rangeApply}
        data-testid="custom-range-apply"
        onClick={apply}
      >
        Show this window
      </button>
      {/* **Cancel restores, and it does so by ceasing to exist.** The component is keyed on the
          window in force, so closing and re-opening mounts a fresh form seeded from that window —
          an abandoned draft cannot be shown because it is not kept. Resetting the fields here as
          well would be a second implementation of one guarantee, and the test for it passed with
          this handler emptied, which is how an assertion comes to be satisfied by its own
          redundancy. */}
      <button
        type="button"
        className={styles.rangeCancel}
        data-testid="custom-range-cancel"
        onClick={close}
      >
        Cancel
      </button>
      {/* **Said where the control is, and announced.** A form that silently does nothing when
          pressed is the failure a reader reports as "the date picker is broken", and they are
          right to. */}
      {refused ? (
        <span className={styles.rangeRefused} role="alert" data-testid="custom-range-refused">
          {refused}
        </span>
      ) : null}
    </span>
  );
}

/** Where the last screen was, so a navigation can be told from a first load. **The shell is mounted
 * again by every screen** (each renders its own `<AppShell>`), so a component's own state cannot
 * remember anything across a click on the rail: these three live at module level because the thing
 * they remember outlives the component.
 *
 * - `lastPath` and `focusOwedUntil` are review finding **F16**. Activating a rail link destroyed the
 *   focused link with the old screen and focus fell to `<body>`: a keyboard user re-tabbed twenty
 *   stops after every navigation and a screen reader announced nothing. Focus now goes to the new
 *   screen's `<h1>`, which is what a screen reader reads as the page's name. It is owed for a short
 *   window rather than once, because a screen may mount its shell more than once while it loads
 *   (the first one destroyed, again, by the second). It is never taken on a first load, and never
 *   from something the person has put focus on since.
 * - `railScroll` keeps the rail where the reader left it, rather than at the top on every click. */
let lastPath: string | null = null;
let focusOwedUntil = 0;
let railScroll = { top: 0, left: 0 };

/** The way the address carries the reader across a link: the method and the window, so a click can
 * never silently change which Scope 2 inventory, or which year, is being read. `undefined` drops a
 * key from the address bar, so a workspace with no period carries none. */
function carried(method: Scope2Method, period: Window | undefined) {
  return (prev: Record<string, unknown>) => ({
    ...prev,
    method,
    from: period?.period_start,
    to: period?.period_end,
  });
}

export function AppShell({
  title,
  crumb,
  memberships,
  membershipsFailed,
  organization,
  onOrganizationChange,
  method,
  period,
  periods,
  onPeriodChange,
  bodyScrolls,
  lights,
  children,
}: Props) {
  const { pathname, search } = useLocation();
  // Which `?tab=` the address carries, for a place whose tabs are views of one path.
  const tabParam = (search as { tab?: unknown }).tab;
  const tabValue = typeof tabParam === "string" ? tabParam : undefined;
  // Who is looking, for the rail. `RequireSession` has already read this, so it is cached and the
  // rail does not flash a different menu; an unknown role is offered Home and Support only.
  const { data: me } = useMe();
  const role = me?.role;
  const railSections = sectionsFor(role);
  const located = locate(pathname, tabValue, lights);
  const isRecordPage = /^\/records\/[^/]+$/.test(pathname);
  const narrowRail = useSyncExternalStore(subscribeNarrowRail, isNarrowRail, serverNarrowRail);
  /** At narrow width the rail is a menu (F6): closed it is one row, open it is a full-screen sheet.
   * Open is "opened on this path", so every navigation closes it (a link in the sheet is one)
   * without an effect to do so; and the sheet only exists while the viewport is narrow. */
  const [menuPath, setMenuPath] = useState<string | null>(null);
  const menuOpen = menuPath === pathname;
  const setMenuOpen = (next: boolean | ((open: boolean) => boolean)) =>
    setMenuPath((at) => {
      const open = typeof next === "function" ? next(at === pathname) : next;
      return open ? pathname : null;
    });
  const menuButton = useRef<HTMLButtonElement>(null);
  const mainColumn = useRef<HTMLDivElement>(null);
  const railId = useId();
  const sheetOpen = narrowRail && menuOpen;
  useEffect(() => {
    // Everything behind the sheet is out of reach: not focusable, not read out, not clickable.
    const column = mainColumn.current;
    if (!column) return;
    column.inert = sheetOpen;
    return () => {
      column.inert = false;
    };
  }, [sheetOpen]);
  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMenuPath(null);
      menuButton.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheetOpen]);
  //: **The tab says where the reader is** (F-008). The same `title` the `<h1>` below prints, so the
  //: heading and the document title cannot name two different pages.
  useDocumentTitle(title);
  const entitiesRead = useEntities();
  const entities = entitiesRead.data ?? [];
  const sites = entities.reduce((sum, entity) => sum + entity.facility_count, 0);
  const groupIds = useId();
  const carry = carried(method, period);
  // Passive, like the badge: the same cache entry, so no request is made for the rail's sake.
  const missingOnRail = useMissingSources(period);

  /** Focus follows a navigation (F16) — see `lastPath` above.
   *
   * **Whose heading.** A page header (`TablePage`, `RecordPage`) draws the screen's `<h1>` itself and
   * claims it from the bar (`PageTitle`); a screen that has not moved over keeps the bar's. Either
   * way exactly one `<h1>` exists and focus goes to it. The claim is made in a layout effect, so the
   * bar's heading is gone before first paint; the effect below runs again when ownership changes, so
   * focus lands on the heading that is left. */
  const heading = useRef<HTMLHeadingElement>(null);
  const pageHeading = useRef<HTMLHeadingElement>(null);
  const [claims, setClaims] = useState(0);
  const headerOwned = claims > 0;
  const claim = useCallback(() => {
    setClaims((count) => count + 1);
    return () => setClaims((count) => count - 1);
  }, []);
  useEffect(() => {
    if (lastPath !== null && lastPath !== pathname) focusOwedUntil = Date.now() + 1500;
    lastPath = pathname;
    const held = document.activeElement;
    if (Date.now() < focusOwedUntil && (held === null || held === document.body)) {
      (pageHeading.current ?? heading.current)?.focus({ preventScroll: true });
    }
  }, [pathname, headerOwned]);

  /** The rail keeps its scroll position across screens, and the current entry is always inside
   * what is visible (F19). Measured by hand rather than `scrollIntoView`: that scrolls every
   * ancestor, and the page's own `overflow: hidden` ancestors can be scrolled by script. */
  const strip = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const list = strip.current;
    if (!list) return;
    list.scrollTop = railScroll.top;
    list.scrollLeft = railScroll.left;
    const current = list.querySelector<HTMLElement>('[aria-current="page"]');
    if (!current) return;
    const at = current.getBoundingClientRect();
    const box = list.getBoundingClientRect();
    if (at.top < box.top) list.scrollTop -= box.top - at.top + 6;
    else if (at.bottom > box.bottom) list.scrollTop += at.bottom - box.bottom + 6;
    if (at.left < box.left) list.scrollLeft -= box.left - at.left + 6;
    else if (at.right > box.right) list.scrollLeft += at.right - box.right + 6;
    railScroll = { top: list.scrollTop, left: list.scrollLeft };
  }, [pathname]);

  /** Whether the rail is reduced to icons. **The person's own choice, and nothing else decides it**
   * (KD's design review, 2026-11-01): not the route - a record page used to collapse it, which moved
   * the whole layout 176px each time a record opened. Until the person has pressed the toggle the
   * default follows the window: icons under 1200px, open above. A choice is read once from storage
   * (`railPrefs.ts` swallows its failures), written on every change, and beats the default either way;
   * the screen never depends on storage being there.
   *
   * A collapsed rail opens over the page when it is pointed at or tabbed into (`peek`) without moving
   * the page, and shuts when it is left. */
  const [choice, setChoice] = useState(readRailChoice);
  const smallWindow = useSyncExternalStore(subscribeSmallWindow, isSmallWindow, serverNarrowRail);
  const [peeked, setPeeked] = useState(false);
  const collapsed = choice ?? smallWindow;
  const peek = collapsed && peeked && !narrowRail;
  /** What is drawn: icons only, unless the rail is open or is being peeked at. */
  const iconOnly = collapsed && !peek;
  /** Whether the person has pressed the control this visit. **Motion confirms a change and is not
   * a greeting**: the rail's words fade in when it is opened or closed, and not on the first paint
   * of every page, where nothing has changed. */
  const [toggled, setToggled] = useState(false);
  const reduce = useReducedMotion();
  const fadeIn = toggled && !reduce ? { initial: { opacity: 0 } } : { initial: false as const };
  const fadeTo = { animate: { opacity: 1 }, transition: { duration: 0.16, ease: "easeOut" as const } };
  const toggleRail = () => {
    setToggled(true);
    setPeeked(false);
    const next = !collapsed;
    setChoice(next);
    writeRailCollapsed(next);
  };
  /** The label shown beside a collapsed entry. State of the rail, not of the entry, because the tip
   * is drawn outside the strip (see `.tip`): the strip scrolls and would clip it. */
  const [tip, setTip] = useState<{ label: string; top: number; left: number } | null>(null);
  const showTip = (label: string, element: HTMLElement) => {
    if (!iconOnly || !window.matchMedia("(min-width: 901px)").matches) return;
    const box = element.getBoundingClientRect();
    setTip({ label, top: box.top + box.height / 2, left: box.right + 8 });
  };

  /** The command palette: Cmd+K or Ctrl+K opens it from anywhere, and the search field in the bar
   * does the same. Toggles, so the shortcut that opened it also closes it.
   *
   * **It is the only search** (review finding F20). The rail used to carry a second field with its
   * own results list beside a "Jump to" button in the bar, two doors to one list with two different
   * no-result behaviours. The bar's field is the door now, it shows the shortcut, and the palette it
   * opens lists the same screens and Help sections the rail field did. */
  const [paletteOpen, setPaletteOpen] = useState(false);
  const paletteButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        !event.shiftKey &&
        event.key.toLowerCase() === "k"
      ) {
        // The browser's own Ctrl+K (focus the address bar's search) is not one this product needs.
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** Whether the custom-range form has replaced the period selector, and how focus gets back.
   *
   * **Focus cannot be moved where the form closes**, because the element to return to does not
   * exist at that moment: the selector is unmounted while the form is open and remounts after. So
   * closing records that focus is owed and an effect pays it on the render where the selector is
   * back — the register's "control that disappears under the reader" problem (design rule 10), in
   * the shell.
   */
  const [rangeOpen, setRangeOpen] = useState(false);
  // A ref rather than state for *whether focus is owed*, because it is not something anything
  // renders — and state set from inside an effect is a second render pass the linter is right to
  // refuse. The effect runs on the commit where the selector is back in the document, which is the
  // first moment there is anything to focus.
  const focusOwed = useRef(false);
  const periodControl = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (rangeOpen || !focusOwed.current) return;
    focusOwed.current = false;
    periodControl.current?.focus();
  }, [rangeOpen]);
  const closeRange = () => {
    focusOwed.current = true;
    setRangeOpen(false);
  };

  /** The workspace's name as the rail prints it. **The suffix is removed only when the tag replaces
   * it**, so the marker appears exactly once and never zero times. The strip used to be
   * unconditional while the tag was conditional, which are two decisions about one fact - and they
   * disagree in the one direction that matters: a workspace whose `legal_name` carries
   * `(SAMPLE DATA)` while `is_sample` is false rendered as a clean, real-looking name with **no
   * marker at all**, because the strip removed the only other signal. That is reachable rather than
   * theoretical: ADR-0079 records that the marker is *derived when requested, not recorded at
   * issue*, and the production refusal rests on `EMITIQ_ENVIRONMENT`, which the database does not
   * enforce. Keeping the name intact is the conservative branch - if the flag and the name
   * disagree, show what the name says. `sampleMarker.test.tsx` asserts both halves. */
  const workspaceLabel = organization
    ? organization.is_sample
      ? organization.legal_name.replace(" (SAMPLE DATA)", "")
      : organization.legal_name
    : "";

  /** The eyebrow above a page's title: the company and the reporting year, `<Company> · FY<year>` -
   * where this page's figures are for, and not the name of the group it sits in. The year is the
   * period's own "FY" word when its label has one, else the year it ends in. */
  const fiscal = period ? fiscalWord(period.name ?? period.label, period.period_end) : undefined;
  const eyebrow = workspaceLabel ? (fiscal ? `${workspaceLabel} · ${fiscal}` : workspaceLabel) : undefined;
  const titleSlot = useMemo<PageTitleSlot>(
    () => ({ headingRef: pageHeading, claim, eyebrow }),
    [claim, eyebrow],
  );

  // The reporting context: the entity and the period are one statement about which figures these
  // are, so they sit together at the right of the bar, apart from the person's own controls. Told
  // apart by what they do: the period is a select (a bordered pill with a caret), and the entity is
  // a statement and has no chrome at all (F21). The Scope 2 method used to be a third member; it left
  // the bar (ADR-0212 decision 5).
  const contextBar = (
    <div className={styles.context} role="group" aria-label="Reporting context">
      {/* An Auditor's scope is always in view: nothing they can reach changes anything (U7). */}
      {role === "auditor" ? (
        <StatusChip tone="info" icon={Eye} testId="read-only-chip">
          Read-only
        </StatusChip>
      ) : null}
      <EntityContext entities={entities} failed={Boolean(entitiesRead.error)} />

      {/* The reporting period, and the way to a window the workspace never declared (EXP-21).
        **In the selector rather than beside it, and that is a width decision as much as a
        design one.** Every item in this bar is context a figure cannot be read without, and
        `make ui-check` measures the bar's own overflow at 820px - so a seventh item would be
        spent on a control rather than on a fact, and the first thing to go would be one of
        the facts. Choosing dates *is* choosing a period, so it is one control. */}
      {rangeOpen ? null : (
        <span className={styles.periodChip}>
          <CalendarBlank className={styles.chipIcon} size={14} aria-hidden="true" />
          <select
            ref={periodControl}
            className={styles.period}
            aria-label="Reporting period"
            data-testid="period"
            value={period ? `${period.period_start}|${period.period_end}` : NO_PERIOD}
            onChange={(event) => {
              if (event.target.value === CUSTOM) {
                setRangeOpen(true);
                return;
              }
              const [start, end] = event.target.value.split("|");
              const chosen = periods.find(
                (candidate) =>
                  candidate.period_start === start && candidate.period_end === end,
              );
              if (chosen) onPeriodChange(chosen);
            }}
          >
            {/* Said, not blank (design rule 5). Present only while no window is in force, so
              it can never sit beside a real period as though it were one of them. */}
            {period ? null : (
              <option value={NO_PERIOD} disabled>
                No reporting period yet
              </option>
            )}
            {periods.map((candidate) => (
              <option
                key={`${candidate.period_start}|${candidate.period_end}`}
                value={`${candidate.period_start}|${candidate.period_end}`}
              >
                {candidate.label}
              </option>
            ))}
            {/* Last, and never the selected value: it is an instruction rather than a
              window, and the ellipsis is the convention that says a form follows. */}
            <option value={CUSTOM}>Custom range…</option>
          </select>
          <CaretDown className={styles.periodCaret} size={12} aria-hidden="true" />
        </span>
      )}

      {/* **Keyed on the window in force.** The key is what makes "cancel restores" a
        property of the structure rather than of a handler somebody has to remember: a
        change of window, or a close and re-open, mounts a fresh form seeded from that
        window, so a draft nobody applied is never kept to be shown later. */}
      <CustomRange
        key={`${period?.period_start ?? ""}|${period?.period_end ?? ""}|${String(rangeOpen)}`}
        period={period}
        open={rangeOpen}
        close={closeRange}
        onPeriodChange={onPeriodChange}
      />
    </div>
  );

  return (
    // **One icon weight for the whole shell, set once** (`regular`, stroke 1.8 at 24px in Phosphor's
    // own drawing). The place you are is marked by the bar, the tint and the ring on its row, none of
    // which is colour alone, so the icon no longer has to change weight to carry it.
    <IconContext.Provider value={{ weight: "regular" }}>
      <PageTitleContext.Provider value={titleSlot}>
      <div className={styles.shell}>
        {/* **First in the document, before the rail** (UX-03, EXP-39).
          The rail holds twenty-one destinations, so the first control on any screen was the
          twenty-second Tab stop - and so was the first control after every navigation, because the
          rail is rendered again. A journey that costs that many keystrokes to start is not one that
          "works without a pointer"; it is one somebody abandons.

          An `<a href>` so it is announced and operated as a link, but it moves focus itself and
          keeps the URL: the router owns the address bar, and a `#screen` left in it is a fragment
          somebody shares that does nothing on arrival. Focusing the target explicitly is also the
          version that works - a fragment jump scrolls, and only moves focus if the target is
          focusable, which is why `<main>` carries `tabIndex={-1}`. */}
        <a
          href="#screen"
          className="visuallyHidden skipLink"
          onClick={(event) => {
            event.preventDefault();
            document.getElementById("screen")?.focus();
          }}
        >
          Skip to {title}
        </a>

        {/* **The slot holds the rail's place in the layout** at the collapsed or the full width, so a
            rail opened over a record page (`peek`) does not move the page. At phone width the rail is
            a menu and the slot is transparent. */}
        <div
          className={styles.railSlot}
          data-collapsed={collapsed}
          data-peek={peek}
          onMouseEnter={() => setPeeked(true)}
          onMouseLeave={() => setPeeked(false)}
          onFocus={() => setPeeked(true)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setPeeked(false);
          }}
        >
        <nav
          id={railId}
          className={styles.rail}
          aria-label="Sections"
          data-collapsed={iconOnly}
          data-menu={narrowRail ? (menuOpen ? "open" : "closed") : undefined}
        >
          <div className={styles.brandRow}>
            <div className={styles.brand}>
              {/* A letter on the accent, not a drawn glyph: the mark is the product's name. */}
              <span className={styles.brandMark} aria-hidden="true">
                E
              </span>
              <motion.span
                key={String(iconOnly)}
                className={styles.brandName}
                {...fadeIn}
                {...fadeTo}
              >
                EmitIQ
              </motion.span>
            </div>
            {narrowRail ? (
              <button
                ref={menuButton}
                type="button"
                className={styles.menuButton}
                data-testid="rail-menu"
                aria-expanded={menuOpen}
                aria-controls={railId}
                onClick={() => setMenuOpen((open) => !open)}
              >
                {menuOpen ? <X size={16} aria-hidden="true" /> : <List size={16} aria-hidden="true" />}
                {menuOpen ? "Close menu" : "Menu"}
              </button>
            ) : null}
          </div>

          {/* **The workspace sits at the top of the rail** (UI redesign, Batch 1): whose figures
              these are is the first thing to know and the card is the switcher. The tag, the
              collapsed tile and the failed-read alert moved with it unchanged. The basis line
              (jurisdictions, GWP set, entities, sites) is the company card at the foot. */}
          {organization ? (
            iconOnly && !narrowRail ? (
              <div className={styles.workspaceMini}>
                {/* The workspace, reduced to a tile. Its name is on the tile's label rather than in
                  a text node, so the expanded card remains the one place the name is *printed*. */}
                <span
                  className={styles.workspaceTile}
                  role="img"
                  aria-label={workspaceLabel}
                  title={workspaceLabel}
                >
                  {workspaceLabel.trim().charAt(0).toUpperCase() || "?"}
                </span>
                {organization.is_sample && !narrowRail ? (
                  <span className={styles.sampleTag} data-testid="sample-tag" title="Sample data">
                    Sample data
                  </span>
                ) : null}
                {membershipsFailed ? (
                  /* The failure is the full card's alert, with its retry; this opens the card. A
                   collapsed rail must not be a place where a failed read goes unsaid. */
                  <button
                    type="button"
                    className={styles.workspaceWarn}
                    aria-label="Your list of workspaces could not be read. Expand the sidebar to try again."
                    title="Your list of workspaces could not be read"
                    onClick={toggleRail}
                  >
                    <Warning size={16} aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            ) : (
              <motion.div className={styles.workspace} {...fadeIn} {...fadeTo}>
                <span className={styles.workspaceName}>{workspaceLabel}</span>
                {organization.is_sample && !narrowRail ? (
                  /* `data-testid`, because the tag's own words are "Sample data" and a text search
                   cannot tell the tag from copy that *mentions* sample data in order to deny it —
                   the trap recorded four times in `CLAUDE.md`. Assert structure. */
                  <span className={styles.sampleTag} data-testid="sample-tag">
                    Sample data
                  </span>
                ) : null}
                {membershipsFailed ? (
                  /* **A failed read is said, in the one treatment** (`ReadFailed`: role=alert, the
                   failure's sentence, a way to ask again). It replaces the switcher rather than
                   sitting beside it: the list is what could not be read, and a stale or absent
                   control beside a warning is two claims about one thing. */
                  <ReadFailed
                    of="Your list of workspaces"
                    error={membershipsFailed.error}
                    onRetry={membershipsFailed.onRetry}
                  />
                ) : memberships.length > 1 ? (
                  /* Switching is a new session, not a query parameter — see `useScreen`. The list
                   comes from this person's memberships, which is the only cross-organisation
                   question the API answers, and it carries a name and a role and no data. */
                  <select
                    className={styles.switcher}
                    aria-label="Workspace"
                    value={organization.id}
                    onChange={(event) => onOrganizationChange(event.target.value)}
                  >
                    {memberships.map((candidate) => (
                      <option key={candidate.org_id} value={candidate.org_id}>
                        {candidate.legal_name}
                      </option>
                    ))}
                  </select>
                ) : null}
              </motion.div>
            )
          ) : null}

          {/* The strip scrolls inside the rail, keeps its place across screens, and says it scrolls
              (the edge shadows in the stylesheet). `onScroll` records where it is for the next
              screen's shell. */}
          <div
            className={styles.nav}
            ref={strip}
            onScroll={(event) => {
              railScroll = {
                top: event.currentTarget.scrollTop,
                left: event.currentTarget.scrollLeft,
              };
            }}
          >
            {railSections.map(({ section, items }, groupAt) => (
              <div
                key={section}
                role="group"
                aria-labelledby={`${groupIds}-g${groupAt}`}
                className={styles.navBlock}
              >
                {/* Six printed headings (ADR-0212; KD's design review dropped "Start"): small,
                    muted-light labels. The first group - Home, Action items, Ask - has a name for
                    assistive technology and no printed heading. A heading is a label for the group
                    and never a page. */}
                <div
                  className={section === "Start" ? "visuallyHidden" : styles.navGroup}
                  id={`${groupIds}-g${groupAt}`}
                >
                  {section}
                </div>
                {items.map((item) => {
                  const Badge = BADGES[item.id];
                  const here = located?.nav.id === item.id;
                  const entry = entryFor(item, role);
                  // The badge's number is about one tab; the item opens on it (U2).
                  const tab = railTabFor(item.id, missingOnRail) ?? entry.tab;
                  return (
                    <Link
                      key={item.id}
                      to={entry.to}
                      className={styles.navItem}
                      data-active={here}
                      aria-current={here ? "page" : undefined}
                      // The method and the period are both carried across navigation: switching
                      // screens must not silently change which Scope 2 inventory, or which year, is
                      // being read.
                      search={
                        tab === undefined
                          ? carry
                          : (prev: Record<string, unknown>) => ({ ...carry(prev), tab })
                      }
                      onMouseEnter={(event) => showTip(item.label, event.currentTarget)}
                      onFocus={(event) => showTip(item.label, event.currentTarget)}
                      onMouseLeave={() => setTip(null)}
                      onBlur={() => setTip(null)}
                    >
                      <item.icon className={styles.navIcon} size={16} aria-hidden="true" />
                      {/* Keyed on the rail's state so a toggle remounts it and the fade plays; the
                          initial paint does not animate. Opacity only, so no layout moves. */}
                      <motion.span
                        key={String(iconOnly)}
                        className={styles.navLabel}
                        {...fadeIn}
                        {...fadeTo}
                      >
                        {item.label}
                      </motion.span>
                      {Badge ? <Badge period={period} /> : null}
                    </Link>
                  );
                })}
              </div>
            ))}
          </div>

          {/* **The company card at the foot of the rail**: the basis every figure is stated on, in one
              line. Counted from the entities read the bar already makes, and absent when that read
              failed: "0 entities" would be a claim about a company, not a failure. Hidden when the
              rail is collapsed or a strip (the basis is also in the top bar's context group). */}
          {organization ? (
            <div className={styles.company} data-testid="company-card">
              <span className={styles.companyLabel}>Reporting basis</span>
              <span className={styles.companyMeta}>
                {/* **Joined from the parts that exist.** A workspace with no company has no
                    jurisdictions, and `"" · AR5` printed as a bare leading dot ("· AR5") on a new
                    Admin's first screen. */}
                {[
                  ...(organization.jurisdictions.length > 0 ? [organization.jurisdictions.join(" · ")] : []),
                  organization.gwp_set,
                  ...(entities.length > 0
                    ? [
                        `${entities.length} ${entities.length === 1 ? "entity" : "entities"}`,
                        `${sites} ${sites === 1 ? "site" : "sites"}`,
                      ]
                    : []),
                ].join(" · ")}
              </span>
            </div>
          ) : null}
        </nav>
        </div>

        {organization?.is_sample && narrowRail ? (
          <div
            className={styles.sampleStatus}
            data-testid="sample-status"
            role="region"
            aria-label="Sample workspace"
          >
            <span className={styles.sampleTag} data-testid="sample-tag">
              Sample data
            </span>
          </div>
        ) : null}

        {tip ? (
          <div className={styles.tip} style={{ top: tip.top, left: tip.left }} aria-hidden="true">
            {tip.label}
          </div>
        ) : null}

        <div className={styles.main} ref={mainColumn}>
          <header className={styles.top}>
            {/* The rail's collapse control lives in the bar and is **one button in both states** so
              focus survives the toggle. Its label says what pressing it does and `aria-expanded` says
              what is true now. The choice is remembered (`railPrefs.ts`) except on a record page,
              where the rail is icons and the button opens it for the visit; at phone width the rail
              is a menu and the button is not drawn. */}
            <button
              type="button"
              className={`${styles.iconButton} ${styles.railToggle}`}
              data-testid="rail-toggle"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!collapsed}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              onClick={toggleRail}
            >
              <List size={16} aria-hidden="true" />
            </button>

            {/* The one search, with its shortcut on it: a shortcut nobody is told about is not one.
              It is a button styled as the field it opens, because the field that takes the typing is
              the palette's own, where the list is. */}
            <button
              ref={paletteButton}
              type="button"
              className={styles.search}
              data-testid="palette-open"
              aria-label="Search screens, Help and records"
              aria-haspopup="dialog"
              aria-expanded={paletteOpen}
              aria-keyshortcuts="Control+K Meta+K"
              onClick={() => setPaletteOpen(true)}
            >
              <MagnifyingGlass size={15} aria-hidden="true" />
              <span className={styles.searchText}>Search</span>
              <kbd className={styles.searchKey}>{shortcutLabel()}</kbd>
            </button>

            {/* Where you are, for a screen that has not moved to a page header: the title with its
              one-line purpose beside it. The `<h1>` is the screen's name (and is where focus lands
              after a navigation, F16). A page header (`PageTitle`) takes it over, and then the bar
              draws none: one `<h1>` per screen. */}
            {headerOwned ? null : (
              <div className={styles.heading}>
                <h1 className={styles.title} ref={heading} tabIndex={-1}>
                  {title}
                </h1>
                {crumb ? <p className={styles.crumb}>{crumb}</p> : null}
              </div>
            )}

            <div className={styles.spacer} />

            {/* The reporting context: the entity and the period, always in the bar. */}
            {contextBar}

            {/* Help for the place you are in: Help's "Where things are" tab, opened at that place's
                section (G-Wadmin-19; the bar's question mark used to open the first tab, a statement
                about hosting, from every screen). A path the shell does not know opens the tab
                without a place. */}
            <Link
              to="/help"
              search={(prev: Record<string, unknown>) => ({
                ...carry(prev),
                tab: "places",
                place: located?.nav.id,
              })}
              className={styles.iconButton}
              data-testid="help-icon"
              aria-label="Help"
              title={located ? `Help: ${located.nav.label}` : "Help"}
            >
              <Question size={16} aria-hidden="true" />
            </Link>

            <ActionBell method={method} period={period} />
            <SessionBadge />
          </header>

          {/* A path back, for a screen reached from another (card C-A, gate 32). Not the `crumb`
            above, which restates what the screen is. */}
          <Breadcrumbs
            pathname={pathname}
            title={title}
            method={method}
            period={period}
            labelOf={labelOfPath}
            lights={lights}
          />

          {/* The strip of tabs under a place: the screens that belong together, one level. A
              screen's own tabs (a record's, Help's) are the second and last level. **A record page
              draws no strip**: it carries its own five tabs, and the place's strip above them was
              a second tab level on one page; the breadcrumb names the place and leads back to it. */}
          {isRecordPage ? null : (
            <SectionTabs pathname={pathname} tab={tabValue} role={role} carry={carry} lights={lights} />
          )}

          {/* A lookup under the screen that failed (the roster, the profile), said once. */}
          <LookupsFailed />

          {/* Named after the screen, and focusable so the skip link can land on it. `aria-label`
            rather than `aria-labelledby` pointing at the `<h1>`: the heading is inside the header
            above, outside this landmark, and a landmark named by an element it does not contain is
            the kind of thing that works in one screen reader and not the next. */}
          <main
            id="screen"
            tabIndex={-1}
            aria-label={title}
            className={bodyScrolls ? `${styles.body} bodyScrolls` : styles.body}
          >
            {children}
          </main>
        </div>

        {/* Presence, so the palette can leave as it arrived. Reduced motion: no transition at all. */}
        <AnimatePresence>
          {paletteOpen ? (
            <CommandPalette
              key="command-palette"
              screens={paletteScreens(role)}
              method={method}
              period={period}
              onClose={() => setPaletteOpen(false)}
              fallbackFocus={paletteButton}
            />
          ) : null}
        </AnimatePresence>
      </div>
      </PageTitleContext.Provider>
    </IconContext.Provider>
  );
}
